import React, { useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FaDraftingCompass } from '@react-icons/all-files/fa/FaDraftingCompass';
import { FaProjectDiagram }  from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaCog }             from '@react-icons/all-files/fa/FaCog';
import { FaHome }            from '@react-icons/all-files/fa/FaHome';
import { FaSitemap }         from '@react-icons/all-files/fa/FaSitemap';
import { FaSpinner }         from '@react-icons/all-files/fa/FaSpinner';
import Breadcrumb from '../../../../commons/Breadcrumb';
import PageHeader from '../../../../commons/PageHeader';
import PageAlert, { usePageAlert } from '../../../../commons/PageAlert';
import Confirm, { useConfirm } from '../../../../commons/Confirm';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { approveDiseno, getDiseno } from '../../../../manager/workflow-def.reducer';
import WorkflowDesigner from '../diseno/workflow-designer';
import { getEntity, reset, updateDiseno } from '../../../../manager/workflow-diseno.reducer';

const LIST = '/configuracion/workflow/definicion-workflow';

export default function WorkflowDisenoPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();

  // const isEmpresaDiseno = pathname.includes('/workflow-empresa/');

  // const parentIcon = isEmpresaDiseno ? <FaNetworkWired /> : ;

  const diseno = useAppSelector(s => s.workflowDef.diseno);
  const loadingDiseno = useAppSelector(s => s.workflowDef.loadingDiseno);
  const approving = useAppSelector(s => s.workflowDef.approvingDiseno);
  const approveSuccess = useAppSelector(s => s.workflowDef.approveSuccess);
  const errorMessage = useAppSelector(s => s.workflowDiseno.errorMessage);
  const updateSuccess = useAppSelector(s => s.workflowDiseno.updateSuccess);


  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  useEffect(() => {
    if (id) dispatch(getDiseno(id));
    return () => { dispatch(reset()); };
  }, [id, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setConfirmLoading(approving); }, [approving, setConfirmLoading]);

  useEffect(() => {
    if (approveSuccess) {
      dismiss();
      showToast('success', 'Diseño aprobado correctamente.');
      if (id) dispatch(getDiseno(id));
      dispatch(reset());
    }
  }, [approveSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      dismiss();
      showToast('success', 'Diseño modificado correctamente.');
      if (id) dispatch(getDiseno(id));
      dispatch(reset());
    }
  }, [updateSuccess]); 

  useEffect(() => {
    console.log('errorMessage', errorMessage);
    if (errorMessage) showToast('error', errorMessage);
  }, [errorMessage]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadState = loadingDiseno ? 'loading'
    : errorMessage && !diseno?.id ? 'error'
      : diseno?.id ? 'loaded'
        : 'idle';

  const handleApprove = () => {
    if (id) dispatch(approveDiseno(id));
  };

  const confirmApprove = () => {
    ask(
      <>¿Confirmas que deseas aprobar el diseño <strong>{diseno?.nombre ?? `#${id}`}</strong>? Esta acción no se puede deshacer.</>,
      handleApprove,
      { title: 'Aprobar Diseño', confirmLabel: 'Aprobar', variant: 'info' },
    );
  };

  const saveEntity = (data: any) => {

    const newEntity = {
      ...diseno,
      data: { ...diseno.data }
    };
    newEntity.data.edges = data.edges;
    newEntity.data.nodos = data.nodos;

    console.log('save -> newEntity', newEntity);

    dispatch(updateDiseno(newEntity));

  }
  const volver = () => {
    navigate(LIST);
  }

  return (
    <div style={s.page}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Configuración', to: '/configuracion', icon: <FaCog /> },
        { label: 'Workflow', to: '/configuracion/workflow', icon: <FaProjectDiagram /> },
        { label: 'Definición Workflow', to: '/configuracion/workflow/definicion-workflow', icon: <FaSitemap /> },
        { label: `Diseño #${id}`, icon: <FaDraftingCompass /> },
      ]} />

      <PageHeader
        icon={<FaDraftingCompass />}
        iconColor="#7c3aed"
        title={diseno?.profaseWorkflow?.nombre ? `${diseno.profaseWorkflow.nombre}` : `Diseño Workflow #${id}`}
        subtitle="Actividades de Workflow"
      />

      {/* Alert / confirm banners */}
      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      {/* Loading state */}
      {loadState === 'loading' && (
        <div style={s.stateBox}>
          <FaSpinner style={{ fontSize: 28, color: '#7c3aed', animation: 'spin 1s linear infinite' }} />
          <span style={s.stateText}>Cargando diseño…</span>
        </div>
      )}

      {/* Error state */}
      {loadState === 'error' && (
        <div style={s.stateBox}>
          <span style={{ ...s.stateText, color: '#dc2626' }}>No se pudo cargar el diseño.</span>
        </div>
      )}

      {/* Content */}
      {loadState === 'loaded' && diseno &&
        <>
          <WorkflowDesigner
            entity={diseno.data}
            saveEntity={(data: any) => { saveEntity(data) }}
            volver={() => { volver()}}
            crearNodos={true} editarTipoNodo={true} editarActividad={true} permitirEliminarNodo={true}
            workflow={diseno.profaseWorkflow}
          />
        </>
      }
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: any }) {
  if (!status) return null;
  const label = typeof status === 'object' ? status.nombre ?? JSON.stringify(status) : String(status);
  const colors: Record<string, { bg: string; text: string }> = {
    APROBADO: { bg: '#dcfce7', text: '#166534' },
    ACTIVO: { bg: '#dcfce7', text: '#166534' },
    PENDIENTE: { bg: '#fef9c3', text: '#854d0e' },
    BORRADOR: { bg: '#f1f5f9', text: '#475569' },
  };
  const c = colors[label.toUpperCase()] ?? { bg: '#eff6ff', text: '#1e40af' };
  return (
    <span style={{
      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: c.bg, color: c.text, letterSpacing: '0.04em',
    }}>
      {label}
    </span>
  );
}

function renderValue(val: any): React.ReactNode {
  if (val === null || val === undefined) return <span style={{ color: '#94a3b8' }}>—</span>;
  if (typeof val === 'boolean') return val ? '✓' : '✗';
  if (typeof val === 'object') {
    if (val.nombre) return `${val.nombre}${val.id ? ` (#${val.id})` : ''}`;
    if (val.id) return `#${val.id}`;
    return JSON.stringify(val);
  }
  return String(val);
}

// ── Styles ─────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
  stateBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '48px 0' },
  stateText: { fontSize: 14, color: '#64748b' },

  card: {
    background: '#fff', borderRadius: 14, padding: '24px 28px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', flexDirection: 'column', gap: 20,
  },
  cardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  cardTitle: { fontSize: 14, fontWeight: 700, color: '#0f172a' },

  fieldGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: '12px 24px',
  },
  field: { display: 'flex', flexDirection: 'column', gap: 2 },
  fieldLabel: { fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' },
  fieldValue: { fontSize: 14, color: '#1e293b' },

  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 4 },
  btnBack: {
    display: 'flex', alignItems: 'center', padding: '9px 18px',
    background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0',
    borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  btnApprove: {
    display: 'flex', alignItems: 'center', padding: '10px 22px',
    background: '#7c3aed', color: '#fff', border: 'none',
    borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(124,58,237,0.3)',
  },
};
